<script setup>
import ProjectCard from '@/Components/ProjectCard.vue'
import { about, contacts, home } from '@/actions/App/Http/Controllers/PageController'
import { index } from '@/actions/App/Http/Controllers/ProjectController'
import { blownInsulation, drywall, screed } from '@/actions/App/Http/Controllers/ServiceController'
import { usePage } from '@inertiajs/vue3'
import { computed, defineComponent, h, onMounted, ref } from 'vue'

defineProps({
    randomProjects: {
        type: Array,
        default: () => [],
    },
})

const page = usePage()
const mainMenu = computed(() => [
    {
        title: 'Domů',
        route: home(),
        arialLabel: 'Domovská stránka',
    },
    {
        title: 'Stavby',
        route: index(),
        arialLabel: 'Portfolio staveb',
    },
    {
        title: 'O nás',
        route: about(),
        arialLabel: 'O našem příbehu',
    },
    {
        title: 'Kontakt',
        route: contacts(),
        arialLabel: 'kontaktujte nás',
    },
])

const services = computed(() => [
    {
        title: 'Montáže sádrokartonu',
        route: drywall(),
        arialLabel: 'Montáže sádrokartonu',
    },
    {
        title: 'Foukaná izolace',
        route: blownInsulation(),
        arialLabel: 'Foukaná izolace',
    },
    {
        title: 'Betonové stěrky',
        route: screed(),
        arialLabel: 'Betonové stěrky',
    },
])

const isMenuOpen = ref(false)

const isScrolledUp = ref(true)

onMounted(() => {
    document.addEventListener('scroll', handleScroll)
})

const handleScroll = () => {
    const currentScroll = window.scrollY || document.documentElement.scrollTop
    const screenHeight = window.innerHeight * 0.6

    if (currentScroll > screenHeight) {
        isScrolledUp.value = false
    } else {
        isScrolledUp.value = true
    }

    handleScroll.prevScroll = currentScroll
}

// Active route detection using Inertia page URL
const getCurrentRoute = () => {
    const currentUrl = page.url
    if (currentUrl === home().url) return 'home.index'
    return page.component
}

const socials = [
    {
        name: 'Facebook',
        href: page.props.socials?.facebook || '#',
        icon: defineComponent({
            render: () =>
                h('svg', { fill: 'currentColor', viewBox: '0 0 24 24' }, [
                    h('path', {
                        'fill-rule': 'evenodd',
                        d: 'M22 12c0-5.523-4.477-10-10-10S2 6.477 2 12c0 4.991 3.657 9.128 8.438 9.878v-6.987h-2.54V12h2.54V9.797c0-2.506 1.492-3.89 3.777-3.89 1.094 0 2.238.195 2.238.195v2.46h-1.26c-1.243 0-1.63.771-1.63 1.562V12h2.773l-.443 2.89h-2.33v6.988C18.343 21.128 22 16.991 22 12z',
                        'clip-rule': 'evenodd',
                    }),
                ]),
        }),
    },
    {
        name: 'Instagram',
        href: page.props.socials?.instagram || '#',
        icon: defineComponent({
            render: () =>
                h('svg', { fill: 'currentColor', viewBox: '0 0 24 24' }, [
                    h('path', {
                        'fill-rule': 'evenodd',
                        d: 'M12.315 2c2.43 0 2.784.013 3.808.06 1.064.049 1.791.218 2.427.465a4.902 4.902 0 011.772 1.153 4.902 4.902 0 011.153 1.772c.247.636.416 1.363.465 2.427.048 1.067.06 1.407.06 4.123v.08c0 2.643-.012 2.987-.06 4.043-.049 1.064-.218 1.791-.465 2.427a4.902 4.902 0 01-1.153 1.772 4.902 4.902 0 01-1.772 1.153c-.636.247-1.363.416-2.427.465-1.067.048-1.407.06-4.123.06h-.08c-2.643 0-2.987-.012-4.043-.06-1.064-.049-1.791-.218-2.427-.465a4.902 4.902 0 01-1.772-1.153 4.902 4.902 0 01-1.153-1.772c-.247-.636-.416-1.363-.465-2.427-.047-1.024-.06-1.379-.06-3.808v-.63c0-2.43.013-2.784.06-3.808.049-1.064.218-1.791.465-2.427a4.902 4.902 0 011.153-1.772A4.902 4.902 0 015.45 2.525c.636-.247 1.363-.416 2.427-.465C8.901 2.013 9.256 2 11.685 2h.63zm-.081 1.802h-.468c-2.456 0-2.784.011-3.807.058-.975.045-1.504.207-1.857.344-.467.182-.8.398-1.15.748-.35.35-.566.683-.748 1.15-.137.353-.3.882-.344 1.857-.047 1.023-.058 1.351-.058 3.807v.468c0 2.456.011 2.784.058 3.807.045.975.207 1.504.344 1.857.182.466.399.8.748 1.15.35.35.683.566 1.15.748.353.137.882.3 1.857.344 1.054.048 1.37.058 4.041.058h.08c2.597 0 2.917-.01 3.96-.058.976-.045 1.505-.207 1.858-.344.466-.182.8-.398 1.15-.748.35-.35.566-.683.748-1.15.137-.353.3-.882.344-1.857.048-1.055.058-1.37.058-4.041v-.08c0-2.597-.01-2.917-.058-3.96-.045-.976-.207-1.505-.344-1.858a3.097 3.097 0 00-.748-1.15 3.098 3.098 0 00-1.15-.748c-.353-.137-.882-.3-1.857-.344-1.023-.047-1.351-.058-3.807-.058zM12 6.865a5.135 5.135 0 110 10.27 5.135 5.135 0 010-10.27zm0 1.802a3.333 3.333 0 100 6.666 3.333 3.333 0 000-6.666zm5.338-3.205a1.2 1.2 0 110 2.4 1.2 1.2 0 010-2.4z',
                        'clip-rule': 'evenodd',
                    }),
                ]),
        }),
    },
    {
        name: 'YouTube',
        href: page.props.socials?.youtube || '#',
        icon: defineComponent({
            render: () =>
                h(
                    'svg',
                    {
                        version: '1.0',
                        xmlns: 'http://www.w3.org/2000/svg',
                        viewBox: '0 0 512 512',
                        fill: 'currentColor',
                        class: 'size-6',
                        'aria-hidden': 'true',
                    },
                    [
                        h(
                            'g',
                            {
                                transform: 'translate(0.000000,512.000000) scale(0.100000,-0.100000)',
                                fill: 'currentColor',
                                stroke: 'none',
                            },
                            [
                                h('path', {
                                    d: 'M1710 4339 c-523 -15 -1002 -54 -1154 -94 -102 -27 -194 -82 -282 -170 -127 -126 -172 -236 -214 -524 -98 -677 -68 -1874 57 -2252 74 -224 248 -379 488 -432 574 -128 3380 -126 3924 3 122 29 231 90 318 176 144 144 196 312 238 775 68 749 20 1746 -98 2037 -85 212 -257 356 -472 397 -415 77 -1697 116 -2805 84z m1007 -1396 c359 -207 653 -379 653 -382 -1 -6 -1304 -761-1314 -761 -3 0 -6 342 -6 760 0 418 3 760 7 760 3 0 300 -170 660 -377z',
                                }),
                            ]
                        ),
                    ]
                ),
        }),
    },
    {
        name: 'TikTok',
        href: page.props.socials?.tiktok || '#',
        icon: defineComponent({
            render: () =>
                h('svg', { fill: 'currentColor', viewBox: '0 0 32 32', xmlns: 'http://www.w3.org/2000/svg' }, [
                    h('path', {
                        d: 'M16.656 1.029c1.637-0.025 3.262-0.012 4.886-0.025 0.054 2.031 0.878 3.859 2.189 5.213l-0.002-0.002c1.411 1.271 3.247 2.095 5.271 2.235l0.028 0.002v5.036c-1.912-0.048-3.71-0.489-5.331-1.247l0.082 0.034c-0.784-0.377-1.447-0.764-2.077-1.196l0.052 0.034c-0.012 3.649 0.012 7.298-0.025 10.934-0.103 1.853-0.719 3.543-1.707 4.954l0.020-0.031c-1.652 2.366-4.328 3.919-7.371 4.011l-0.014 0c-0.123 0.006-0.268 0.009-0.414 0.009-1.73 0-3.347-0.482-4.725-1.319l0.040 0.023c-2.508-1.509-4.238-4.091-4.558-7.094l-0.004-0.041c-0.025-0.625-0.037-1.25-0.012-1.862 0.49-4.779 4.494-8.476 9.361-8.476 0.547 0 1.083 0.047 1.604 0.136l-0.056-0.008c0.025 1.849-0.050 3.699-0.050 5.548-0.423-0.153-0.911-0.242-1.42-0.242-1.868 0-3.457 1.194-4.045 2.861l-0.009 0.030c-0.133 0.427-0.21 0.918-0.21 1.426 0 0.206 0.013 0.41 0.037 0.61l-0.002-0.024c0.332 2.046 2.086 3.59 4.201 3.59 0.061 0 0.121-0.001 0.181-0.004l-0.009 0c1.463-0.044 2.733-0.831 3.451-1.994l0.010-0.018c0.267-0.372 0.45-0.822 0.511-1.311l0.001-0.014c0.125-2.237 0.075-4.461 0.087-6.698 0.012-5.036-0.012-10.060 0.025-15.083z',
                    }),
                ]),
        }),
    },
]
</script>

<template>
    <header class="fixed top-0 left-0 z-999 w-full">
        <div :class="'hidden flex-col lg:flex ' + (isMenuOpen ? 'h-100vh' : '')">
            <nav
                :class="[
                    isScrolledUp && getCurrentRoute() === 'home.index' ? 'bg-white/20' : 'bg-black/50',
                    'mx-2 mt-5 grid grid-cols-12 place-content-center rounded-full px-4 filter backdrop-blur-lg transition-all duration-300 ease-linear lg:px-0 xl:mx-5',
                ]"
            >
                <ul class="col-span-2 mr-auto grid p-2 lg:w-6/12 xl:w-4/12">
                    <li :class="[isMenuOpen ? 'bg-brand-orange' : 'bg-transparent', 'overflow-hidden rounded-full']">
                        <button
                            aria-pressed="false"
                            onclick="this.setAttribute('aria-pressed', !(this.getAttribute('aria-pressed') === 'true'))"
                            class="group flex size-full cursor-pointer items-center justify-center text-white focus:outline-none"
                            aria-label="Otevřít menu"
                            @click="isMenuOpen = !isMenuOpen"
                        >
                            <svg
                                class="pointer-events-none size-8 fill-current"
                                viewBox="0 0 16 16"
                                xmlns="http://www.w3.org/2000/svg"
                            >
                                <rect
                                    class="origin-center translate-x-[7px] -translate-y-[5px] transition-all duration-300 ease-[cubic-bezier(.5,.85,.25,1.1)] group-[[aria-pressed=true]]:translate-x-0 group-[[aria-pressed=true]]:translate-y-0 group-[[aria-pressed=true]]:rotate-[315deg]"
                                    y="7"
                                    width="9"
                                    height="2"
                                    rx="1"
                                />
                                <rect
                                    class="origin-center transition-all duration-300 ease-[cubic-bezier(.5,.85,.25,1.8)] group-[[aria-pressed=true]]:rotate-45"
                                    y="7"
                                    width="16"
                                    height="2"
                                    rx="1"
                                />
                                <rect
                                    class="origin-center translate-y-[5px] transition-all duration-300 ease-[cubic-bezier(.5,.85,.25,1.1)] group-[[aria-pressed=true]]:translate-y-0 group-[[aria-pressed=true]]:rotate-[135deg]"
                                    y="7"
                                    width="9"
                                    height="2"
                                    rx="1"
                                />
                            </svg>
                        </button>
                    </li>
                </ul>

                <ul class="col-span-8 m-auto">
                    <li>
                        <Link
                            :href="home.url()"
                            aria-label="J&M.Sádrokartony s.r.o."
                        >
                            <img
                                src="/src/logo/logo.png"
                                class="h-20 py-2"
                                alt="J&M.Sádrokartony s.r.o. logo"
                            />
                        </Link>
                    </li>
                </ul>

                <ul class="col-span-2 grid p-2">
                    <li class="grid">
                        <Link
                            class="bg-brand-orange flex cursor-pointer items-center justify-center rounded-full"
                            :href="contacts.url() + '#kontaktni-formular'"
                            aria-label="Sejít se"
                        >
                            <span class="font-chivo text-2xl text-white uppercase xl:text-3xl"> Sejít se </span>
                        </Link>
                    </li>
                </ul>
            </nav>

            <Transition
                enter-active-class="ease-out duration-300 "
                enter-from-class="opacity-0 transform -translate-x-full  "
                enter-to-class="opacity-100 transform translate-0 backdrop-blur-3xl"
                leave-active-class="ease-in duration-200"
                leave-from-class="opacity-100 transform translate-0"
                leave-to-class="opacity-0 transform translate-x-full"
            >
                <nav
                    v-show="isMenuOpen"
                    class="fixed inset-0 top-[100px] m-5 grid"
                >
                    <div
                        :class="[
                            isScrolledUp && getCurrentRoute() === 'home.index' ? 'bg-white/20' : 'bg-black/50',
                            '3xl:px-32 grid grid-cols-2 rounded-[2rem] py-5 filter backdrop-blur-3xl transition-all duration-300 ease-linear xl:px-20',
                        ]"
                    >
                        <ul class="3xl:space-y-0 flex flex-col items-start justify-center space-y-5 xl:space-y-2">
                            <li
                                v-for="item in mainMenu"
                                :key="item.title"
                                class="hover:bg-brand-orange grid w-full max-w-xl rounded-full"
                            >
                                <Link
                                    :href="item.route"
                                    :arial-label="item.title"
                                    prefetch
                                    cache-for="20s"
                                    class="3xl:text-5xl font-chivo 3xl:py-4 w-full px-4 text-left font-bold tracking-wide text-white uppercase lg:text-2xl xl:py-2 xl:text-5xl 2xl:text-4xl"
                                >
                                    {{ item.title }}
                                </Link>
                            </li>

                            <hr class="my-2 w-full border-zinc-300/30" />

                            <li
                                v-for="item in services"
                                :key="item.title"
                                class="hover:bg-brand-orange grid w-full max-w-xl rounded-full"
                            >
                                <Link
                                    :href="item.route"
                                    :arial-label="item.title"
                                    prefetch
                                    cache-for="20s"
                                    class="font-chivo w-full px-4 text-left font-bold tracking-wide text-white uppercase lg:text-2xl xl:py-3 xl:text-2xl"
                                >
                                    {{ item.title }}
                                </Link>
                            </li>
                        </ul>

                        <ul class="3xl:space-y-10 flex flex-col items-end justify-center space-y-5">
                            <li class="grid grid-cols-1 gap-5 lg:pr-5 xl:grid-cols-2 xl:pr-0">
                                <h2
                                    class="3xl:text-6xl font-chivo col-span-full text-center font-bold tracking-wide text-white uppercase lg:text-2xl 2xl:text-4xl"
                                >
                                    Stavby
                                </h2>

                                <ProjectCard
                                    v-for="product in randomProjects"
                                    :key="product.id"
                                    :project="product"
                                    productTextColor="text-white"
                                    productDateColor="text-gray-300"
                                />
                            </li>
                        </ul>
                    </div>

                    <ul class="absolute right-5 bottom-5 flex space-x-2">
                        <li
                            v-for="social in socials"
                            :key="social.name"
                        >
                            <a
                                :href="social.href"
                                class="flex size-10 items-center justify-center rounded-full bg-white/20 p-1 text-white transition-colors duration-300 hover:bg-white/50"
                                :aria-label="social.name"
                                target="_blank"
                                rel="noopener noreferrer"
                            >
                                <component :is="social.icon" />
                            </a>
                        </li>
                    </ul>
                </nav>
            </Transition>
        </div>

        <div :class="'flex flex-col lg:hidden ' + (isMenuOpen ? 'h-[100vh]' : 'h-fit')">
            <nav
                :class="[
                    isScrolledUp && getCurrentRoute() === 'home.index' ? 'bg-white/20' : 'bg-black/50',
                    'm-2 grid grid-cols-1 rounded-full filter backdrop-blur-lg transition-all duration-300 ease-linear',
                ]"
            >
                <ul class="mx-auto">
                    <li>
                        <Link
                            :href="home.url()"
                            aria-label="J&M.Sádrokartony s.r.o."
                            prefetch
                            cache-for="20s"
                        >
                            <img
                                src="/src/logo/logo.png"
                                class="h-18 py-2"
                                alt="J&M.Sádrokartony s.r.o. logo"
                            />
                        </Link>
                    </li>
                </ul>

                <ul class="absolute top-1/2 right-5 -translate-y-1/2">
                    <li>
                        <button
                            aria-pressed="false"
                            onclick="this.setAttribute('aria-pressed', !(this.getAttribute('aria-pressed') === 'true'))"
                            class="group flex h-full w-full cursor-pointer items-center justify-center text-white focus:outline-none"
                            aria-label="Otevřít menu"
                            @click="isMenuOpen = !isMenuOpen"
                        >
                            <svg
                                class="pointer-events-none size-8 fill-current"
                                viewBox="0 0 16 16"
                                xmlns="http://www.w3.org/2000/svg"
                            >
                                <rect
                                    class="origin-center translate-x-[7px] -translate-y-[5px] transition-all duration-300 ease-[cubic-bezier(.5,.85,.25,1.1)] group-[[aria-pressed=true]]:translate-x-0 group-[[aria-pressed=true]]:translate-y-0 group-[[aria-pressed=true]]:rotate-[315deg]"
                                    y="7"
                                    width="9"
                                    height="2"
                                    rx="1"
                                />
                                <rect
                                    class="origin-center transition-all duration-300 ease-[cubic-bezier(.5,.85,.25,1.8)] group-[[aria-pressed=true]]:rotate-45"
                                    y="7"
                                    width="16"
                                    height="2"
                                    rx="1"
                                />
                                <rect
                                    class="origin-center translate-y-[5px] transition-all duration-300 ease-[cubic-bezier(.5,.85,.25,1.1)] group-[[aria-pressed=true]]:translate-y-0 group-[[aria-pressed=true]]:rotate-[135deg]"
                                    y="7"
                                    width="9"
                                    height="2"
                                    rx="1"
                                />
                            </svg>
                        </button>
                    </li>
                </ul>
            </nav>

            <Transition
                enter-active-class="ease-out duration-300"
                enter-from-class="opacity-0 transform -translate-x-full "
                enter-to-class="opacity-100 transform translate-0 backdrop-blur-3xl"
                leave-active-class="ease-in duration-200"
                leave-from-class="opacity-100 transform translate-0"
                leave-to-class="opacity-0 transform translate-x-full"
            >
                <nav
                    v-if="isMenuOpen"
                    class="fixed inset-0 top-[80px] m-2 grid"
                >
                    <div
                        :class="[
                            isScrolledUp && getCurrentRoute() === 'home.index' ? 'bg-white/20' : 'bg-black/50',
                            'grid grid-cols-1 rounded-[2rem] py-5 filter backdrop-blur-3xl transition-all duration-300 ease-linear',
                        ]"
                    >
                        <ul class="flex flex-col items-center justify-center px-2">
                            <li
                                v-for="item in mainMenu"
                                :key="item.title"
                                class="py-3"
                            >
                                <Link
                                    :href="item.route"
                                    :arial-label="item.title"
                                    prefetch
                                    cache-for="20s"
                                    class="font-chivo text-3xl font-bold tracking-wide text-white uppercase hover:underline"
                                >
                                    {{ item.title }}
                                </Link>
                            </li>

                            <hr class="my-2 w-full border-zinc-300/30" />

                            <li
                                v-for="item in services"
                                :key="item.title"
                                class="py-3"
                            >
                                <Link
                                    :href="item.route"
                                    :arial-label="item.title"
                                    prefetch
                                    cache-for="20s"
                                    class="font-chivo text-xl font-bold tracking-wide text-white uppercase hover:underline"
                                >
                                    {{ item.title }}
                                </Link>
                            </li>
                        </ul>
                    </div>
                </nav>
            </Transition>
        </div>
    </header>
</template>

<style scoped>
.list-move, /* apply transition to moving elements */
.list-enter-active,
.list-leave-active {
    transition: all 0.5s ease;
}

.list-enter-from,
.list-leave-to {
    opacity: 0;
    transform: translateX(30px);
}

/* ensure leaving items are taken out of layout flow so that moving
   animations can be calculated correctly. */
.list-leave-active {
    position: absolute;
}
</style>
